import { getSahBudgetForClient } from './award-data/sah';
import { resolveClientFundingContext } from './client-funding-context';

export function resolveDisplayedAnnualBudget(input: {
  canonicalAnnual?: number | null;
  planAnnual?: number | null;
}): number {
  const canonicalAnnual = Number(input.canonicalAnnual ?? 0);
  if (canonicalAnnual > 0) return canonicalAnnual;
  return 0;
}

export type PortalBudgetClientProjection = {
  classification_level?: number | null;
  funding_programs?: string[] | null;
  hardship_flag?: boolean | null;
  sah_means_tier?: string | null;
  sah_assessed_rate_independence?: number | null;
  sah_assessed_rate_everyday_living?: number | null;
  sah_is_grandfathered?: boolean | null;
  is_grandfathered_hcp?: boolean | null;
  transitioned_hcp_level?: number | null;
};

export type PortalBudgetCarePlanProjection = {
  id?: string | null;
  plan_type?: string | null;
  classification_level?: number | null;
  budget_total?: number | null;
  budget_spent?: number | null;
  budget_remaining?: number | null;
  status?: string | null;
  quarterly_carry_over?: number | null;
  care_management_budget?: number | null;
};

export type PortalBudgetCurrentProjection = {
  id?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  allocated_amount?: number | null;
  spent_amount?: number | null;
  remaining_amount?: number | null;
  unspent_amount?: number | null;
  carry_over_in?: number | null;
  hcp_legacy_carry_forward?: number | null;
  care_management_amount?: number | null;
  /** Quarter snapshot of primary person-centred SAH supplements (Aged Care Rules
   * 2025 s 193-5 rollover base). Returned by get_portal_sah_budget_summary_controlled's
   * currentBudget.primary_supplement_amount when sourced from sah_quarter_budgets. */
  primary_supplement_amount?: number | null;
};

export type PortalCanonicalBudgetDisplay = {
  planId: string | null;
  classification: number | null;
  budgetSource: 'transitioned' | 'standard' | 'unclassified';
  budgetLabel: string;
  annualBudget: number;
  quarterlyAllocation: number;
  quarterlySpent: number;
  quarterlyRemaining: number;
  carryOver: number;
  /** Quarantined legacy HCP carried-over pool for transitioned participants. */
  hcpLegacyCarryForward: number;
  careManagementAllocation: number;
  /** Primary person-centred supplement amount for the current quarter (s 193-5 rollover base). */
  primarySupplementAmount: number;
};

type PortalBudgetFundingContext = ReturnType<typeof resolveClientFundingContext>;

function asNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function roundPortalMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function resolvePortalCanonicalBudgetDisplay(input: {
  client: PortalBudgetClientProjection | null;
  plan: PortalBudgetCarePlanProjection | null;
  budgetRow: PortalBudgetCurrentProjection | null;
  fundingContext?: PortalBudgetFundingContext | null;
}): PortalCanonicalBudgetDisplay | null {
  const { client, plan, budgetRow } = input;
  if (!plan || !budgetRow) return null;

  const fundingContext =
    input.fundingContext ??
    resolveClientFundingContext({
      fundingPrograms: client?.funding_programs ?? null,
      fundingContext: null,
      classificationLevel: client?.classification_level ?? plan?.classification_level ?? null,
      transitionedHcpLevel: client?.transitioned_hcp_level ?? null,
      isGrandfatheredHcp: client?.is_grandfathered_hcp === true,
      sahIsGrandfathered: client?.sah_is_grandfathered === true,
    });
  const budgetResolution = getSahBudgetForClient({
    classificationLevel: fundingContext.isTransitioner
      ? null
      : (client?.classification_level ?? plan?.classification_level ?? null),
    transitionedHcpLevel: fundingContext.isTransitioner
      ? (client?.transitioned_hcp_level ?? null)
      : null,
    // A stored quarter is shown against the schedule in force when it began.
    asOfDate: budgetRow.period_start ?? null,
  });
  const annualBudget = resolveDisplayedAnnualBudget({
    canonicalAnnual: budgetResolution.annual,
  });
  const quarterlySpent = asNumber(budgetRow.spent_amount);
  const quarterlyAllocation = asNumber(budgetRow.allocated_amount);
  const carryOver = asNumber(budgetRow.carry_over_in);
  const hcpLegacyCarryForward = asNumber(budgetRow.hcp_legacy_carry_forward);
  const exposedRemaining =
    budgetRow.remaining_amount != null ? budgetRow.remaining_amount : budgetRow.unspent_amount;
  const quarterlyRemaining =
    exposedRemaining != null
      ? asNumber(exposedRemaining)
      : Math.max(0, roundPortalMoney(quarterlyAllocation + carryOver - quarterlySpent));
  const careManagementAllocation =
    budgetRow.care_management_amount != null
      ? asNumber(budgetRow.care_management_amount)
      : roundPortalMoney(quarterlyAllocation / 9);
  const primarySupplementAmount = asNumber(budgetRow.primary_supplement_amount);

  return {
    planId: typeof plan.id === 'string' ? plan.id : null,
    classification: plan.classification_level ?? client?.classification_level ?? null,
    budgetSource: budgetResolution.source,
    budgetLabel: budgetResolution.label,
    annualBudget,
    quarterlyAllocation,
    quarterlySpent,
    quarterlyRemaining,
    carryOver,
    hcpLegacyCarryForward,
    careManagementAllocation,
    primarySupplementAmount,
  };
}
